import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, ChevronDown, ChevronUp, PackageOpen, Truck, AlertTriangle } from "lucide-react";
import { ReviewValueRow } from "./ReviewValueRow";
import { ProductListEditor } from "./ProductListEditor";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryLabel } from "@/translations";
import { ORDER_CATEGORIES, CURRENCY } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { ParsedOrderData, MissingInfoReason } from "@/types/order";

interface Product {
  name: string;
  quantity?: string;
  unit?: string;
  price?: string;
}

interface OrderReviewSummaryProps {
  data: ParsedOrderData;
  onDataChange: (data: ParsedOrderData) => void;
  onStartOver: () => void;
  className?: string;
}

// Maps MissingInfoReason enum values to translation keys
const REASON_TRANSLATION_KEYS: Record<MissingInfoReason, string> = {
  messenger_chat: "reasonMessengerChat",
  will_provide_later: "reasonWillProvideLater",
  known_customer: "reasonKnownCustomer",
  pickup_order: "reasonPickupOrder",
  not_mentioned: "reasonNotMentioned",
  not_needed: "reasonNotNeeded",
};

export function OrderReviewSummary({
  data,
  onDataChange,
  onStartOver,
  className,
}: OrderReviewSummaryProps) {
  const { language, t } = useLanguage();
  const [isEditingProducts, setIsEditingProducts] = useState(false);

  const products: Product[] = data.products || [];
  const fulfillmentType = data.fulfillment_type || "undetermined";
  const missingReasons = data.missing_info_reasons;
  const isPickup = fulfillmentType === "pickup";
  const warnings = data.warnings || [];
  const hasPartialPayment = warnings.includes("partial_payment");

  const handleProductsChange = (newProducts: Product[]) => {
    onDataChange({
      ...data,
      products: newProducts.map(p => ({
        name: p.name,
        quantity: p.quantity,
        unit: p.unit,
        price: p.price,
      })),
    });
  };

  const calculatedTotal = products.reduce((sum, p) => {
    const qty = parseFloat(p.quantity || "1") || 1;
    const price = parseFloat(p.price || "0") || 0;
    return sum + (qty * price);
  }, 0);

  const displayTotal = data.total_price ?? calculatedTotal;

  // Helper to get translated reason text for a missing field
  function getReasonText(field: "phone" | "address"): string | null {
    const reason = missingReasons?.[field];
    if (!reason) return null;
    const key = REASON_TRANSLATION_KEYS[reason];
    return key ? t(`orders.labels.${key}`) : null;
  }

  return (
    <div className={cn("space-y-5", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          {t("orders.labels.extractedData")}
        </h3>
        <button
          type="button"
          onClick={onStartOver}
          className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          {t("orders.labels.startOver")}
        </button>
      </div>

      {/* Fulfillment type chip */}
      {fulfillmentType !== "undetermined" && (
        <div className="flex items-center gap-2 pb-2">
          {isPickup ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <PackageOpen className="h-3 w-3" />
              {t("orders.labels.fulfillmentPickup")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              <Truck className="h-3 w-3" />
              {t("orders.labels.fulfillmentDelivery")}
            </span>
          )}
        </div>
      )}

      {/* Order Reference */}
      {data.order_number && (
        <div className="pb-3 border-b border-border">
          <ReviewValueRow
            label={t("orders.labels.orderNumber")}
            value={data.order_number}
            onValueChange={(v) => onDataChange({ ...data, order_number: v })}
            placeholder="#"
          />
        </div>
      )}

      {/* Customer Info Section */}
      <div className="space-y-0">
        <ReviewValueRow
          label={t("orders.labels.realName")}
          value={data.customer_name}
          onValueChange={(v) => onDataChange({ ...data, customer_name: v })}
          placeholder={t("orders.placeholders.fullName")}
        />
        {/* Phone: show reason text if missing */}
        {data.customer_phone ? (
          <ReviewValueRow
            label={t("orders.labels.phoneIcon")}
            value={data.customer_phone}
            onValueChange={(v) => onDataChange({ ...data, customer_phone: v })}
            placeholder={t("orders.placeholders.customerPhone")}
            type="tel"
          />
        ) : (
          <div className="flex items-center justify-between py-2.5 border-b border-border/50">
            <span className="text-sm text-muted-foreground">{t("orders.labels.phoneIcon")}</span>
            <span className="text-sm italic text-muted-foreground/60">
              {getReasonText("phone") || t("orders.labels.reasonNotMentioned")}
            </span>
          </div>
        )}
        {data.social_name && (
          <ReviewValueRow
            label={t("orders.labels.socialName")}
            value={data.social_name}
            onValueChange={(v) => onDataChange({ ...data, social_name: v })}
            placeholder="@username"
          />
        )}
        {/* Address: hide for pickup, show reason if missing for delivery */}
        {isPickup ? null : data.address ? (
          <ReviewValueRow
            label={t("orders.labels.deliveryAddress")}
            value={data.address}
            onValueChange={(v) => onDataChange({ ...data, address: v })}
            placeholder={t("orders.placeholders.address")}
          />
        ) : (
          <div className="flex items-center justify-between py-2.5 border-b border-border/50">
            <span className="text-sm text-muted-foreground">{t("orders.labels.deliveryAddress")}</span>
            <span className="text-sm italic text-muted-foreground/60">
              {getReasonText("address") || t("orders.labels.reasonNotMentioned")}
            </span>
          </div>
        )}
      </div>

      {/* Products Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t("orders.labels.products")}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingProducts(!isEditingProducts)}
            className="h-7 text-xs gap-1.5"
          >
            {isEditingProducts ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                {t("common.close")}
              </>
            ) : (
              <>
                <Pencil className="h-3.5 w-3.5" />
                {t("bulk.actions.edit")}
              </>
            )}
          </Button>
        </div>

        {isEditingProducts ? (
          <div className="rounded-lg bg-muted/30 p-3">
            <ProductListEditor
              products={products.map(p => ({
                name: p.name,
                quantity: String(p.quantity ?? "1"),
                unit: p.unit || "",
                price: String(p.price ?? ""),
              }))}
              onProductsChange={(newProducts) =>
                handleProductsChange(newProducts.map(p => ({
                  name: p.name,
                  quantity: p.quantity,
                  unit: p.unit,
                  price: p.price,
                })))
              }
            />
          </div>
        ) : (
          <div className="rounded-lg bg-muted/30 p-3 space-y-1.5">
            {products.length > 0 ? (
              products.map((product, idx) => {
                const priceValue = product.price;
                const hasPrice = priceValue !== null && priceValue !== undefined && priceValue !== "";
                
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">
                      • {product.name}
                      {product.quantity && ` × ${product.quantity}`}
                      {product.unit && ` ${product.unit}`}
                    </span>
                    {hasPrice && (
                      <span className="font-medium text-foreground tabular-nums">
                        {priceValue} {CURRENCY.symbol}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t("orders.labels.noProductsHint")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Category */}
      <div className="flex items-center justify-between py-2.5 border-b border-border/50">
        <span className="text-sm text-muted-foreground">
          {t("orders.labels.category")}
        </span>
        <Select
          value={data.category || ""}
          onValueChange={(v) => onDataChange({ ...data, category: v })}
        >
          <SelectTrigger className="h-8 w-auto min-w-[120px] border-none bg-transparent font-medium text-right pr-2">
            <SelectValue placeholder={t("orders.placeholders.selectCategory")} />
          </SelectTrigger>
          <SelectContent>
            {ORDER_CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {getCategoryLabel(cat, language)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Total Price */}
      <ReviewValueRow
        label={t("orders.labels.totalPrice")}
        value={displayTotal || undefined}
        displayValue={displayTotal ? `${displayTotal.toFixed(2)}` : undefined}
        suffix={CURRENCY.symbol}
        onValueChange={(v) => onDataChange({ ...data, total_price: parseFloat(v) || undefined })}
        type="number"
      />

      {/* Partial payment warning */}
      {hasPartialPayment && (
        <div className="flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("orders.labels.partialPaymentHint")}
        </div>
      )}

      {/* CSR / Posted By */}
      {data.csr_name && (
        <ReviewValueRow
          label={t("orders.labels.postedBy")}
          value={data.csr_name}
          onValueChange={(v) => onDataChange({ ...data, csr_name: v })}
        />
      )}

      {/* Comments */}
      {data.comments && (
        <ReviewValueRow
          label={t("orders.labels.commentsIcon")}
          value={data.comments}
          onValueChange={(v) => onDataChange({ ...data, comments: v })}
          placeholder={t("orders.placeholders.deliveryNotes")}
        />
      )}
    </div>
  );
}
